/**
 * Motion helpers for the marketing pages. All motion is skipped under
 * prefers-reduced-motion.
 *
 * The public routes ship prerendered HTML that is already painted when the SPA
 * mounts (src/main.tsx re-mounts rather than hydrating). An entrance animation
 * on that first mount would make visible text vanish and fade back in, so
 * entrances run only on client-side navigations. `bootedFromPrerender` is read
 * once at module evaluation, before createRoot clears the prerendered markup.
 */
const bootedFromPrerender =
  typeof document !== "undefined" && !!document.getElementById("root")?.firstElementChild;

let firstMarketingMount = true;

export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/** True when an entrance animation should play for this mount. Call once per mount. */
export function shouldAnimateEntrance(): boolean {
  const skip = firstMarketingMount && bootedFromPrerender;
  firstMarketingMount = false;
  return !skip && !prefersReducedMotion();
}

const EASE = "cubic-bezier(.2,.7,.2,1)";

/** Fade-and-rise: label → headline → copy, 240ms with a 90ms stagger. */
export function revealSequence(elements: Element[], baseDelay = 80) {
  elements.forEach((el, i) => {
    el.animate?.([{ opacity: 0, translate: "0 14px" }, { opacity: 1, translate: "0 0" }], {
      duration: 240,
      delay: baseDelay + i * 90,
      easing: EASE,
      fill: "backwards",
    });
  });
}

export function riseIn(el: Element, delay: number) {
  el.animate?.([{ opacity: 0, translate: "0 48px" }, { opacity: 1, translate: "0 0" }], {
    duration: 240,
    delay,
    easing: EASE,
    fill: "backwards",
  });
}

export function crossfade(el: Element | null) {
  if (!el || prefersReducedMotion()) return;
  el.animate?.([{ opacity: 0, translate: "0 8px" }, { opacity: 1, translate: "0 0" }], {
    duration: 200,
    easing: EASE,
  });
}
